// The direct (sphinx server) lane's model, chosen at the top of the surface —
// the composer's bottom model picker is gone from the Playground (the column
// headers already carry per-client picks, so the surface said it twice).
// Writes through `replaceLeadModelId`, the same primitive the per-chat model
// restore uses, so the picker, the send path, and the persisted arrays agree.
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@sphinx/design-system/dropdown-menu";
import { cn } from "@/lib/utils";
import { replaceLeadModelId } from "@/lib/selected-model-storage";
import { SPHINX_MODELS } from "@/shared/sphinx";
import { modelLabel } from "./PlaygroundClientModelPicker";

interface PlaygroundDirectModelPickerProps {
  /** The lead model id currently selected for the direct lane. */
  modelId: string | null;
  /**
   * The session has started: the model is part of what this chat IS, so the
   * picker gives way to a static label. A new chat unlocks it again.
   */
  locked?: boolean;
  className?: string;
}

export function PlaygroundDirectModelPicker({
  modelId,
  locked = false,
  className,
}: PlaygroundDirectModelPickerProps) {
  if (locked) {
    return (
      <span
        className={cn(
          "inline-flex max-w-[9rem] items-center rounded-full border border-border/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground",
          className
        )}
        data-testid="playground-direct-model"
        data-model={modelId ?? ""}
      >
        <span className="truncate">{modelId ? modelLabel(modelId) : ""}</span>
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex max-w-[9rem] items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
            className
          )}
          data-testid="playground-direct-model"
          data-model={modelId ?? ""}
          aria-label="Model for direct chat"
        >
          <span className="truncate">
            {modelId ? modelLabel(modelId) : "Select model"}
          </span>
          <ChevronDown className="size-3 shrink-0" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {SPHINX_MODELS.map((option) => (
          <DropdownMenuItem
            key={option}
            onSelect={() => replaceLeadModelId(option)}
            data-testid={`playground-direct-model-option-${option}`}
            className={cn("text-xs", option === modelId && "font-semibold")}
          >
            {modelLabel(option)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
